import { orientationOf, sizeOf, transformFor } from './exif';

/**
 * Собирает снимки в один многостраничный PDF — порт `PhotoPdfBuilder` Android-версии.
 *
 * Том дела это **один документ**, а не двадцать четыре строки в списке: юрист снимает
 * материалы подряд и работает с ними как с одной папкой на столе.
 *
 * Каждый снимок разворачивается по EXIF и уменьшается до разумной страницы: держать
 * в памяти двадцать кадров по двенадцать мегапикселей нельзя, а читать документ это
 * не мешает.
 */

/** Сторона страницы: текст на снимке документа остаётся читаемым, а память — конечной. */
const MAX_SIDE_PX = 2200;

/** Пережатие страницы. 0.85 — предел, за которым на сканах появляются разводы. */
const JPEG_QUALITY = 0.85;

export interface PhotoPdf {
  content: Blob;
  pageCount: number;
}

export async function buildPdfFromPhotos(photos: Blob[]): Promise<PhotoPdf | null> {
  if (photos.length === 0) return null;

  const { PDFDocument } = await import('pdf-lib');
  const pdf = await PDFDocument.create();

  for (const photo of photos) {
    const page = await toJpegPage(photo);
    if (!page) continue;

    const embedded = await pdf.embedJpg(page.bytes);
    pdf.addPage([page.width, page.height]).drawImage(embedded, {
      x: 0,
      y: 0,
      width: page.width,
      height: page.height,
    });
  }

  if (pdf.getPageCount() === 0) return null;

  const bytes = await pdf.save();
  return {
    // Копия в свой буфер: `save` отдаёт вид на память pdf-lib, а `Blob` требует
    // именно `ArrayBuffer` — на общей памяти (`SharedArrayBuffer`) типы расходятся.
    content: new Blob([new Uint8Array(bytes).buffer], { type: 'application/pdf' }),
    pageCount: pdf.getPageCount(),
  };
}

/**
 * Снимок → страница: разворот по EXIF, уменьшение, пережатие в JPEG.
 *
 * **Кто поворачивает — решается по факту, а не по вере в опцию.** WebKit разворачивает
 * снимок по EXIF сам и `imageOrientation: 'none'` при этом игнорирует: попросив
 * «не поворачивать», мы получали уже развёрнутый кадр и поворачивали его второй раз —
 * страница снова ложилась боком. Поэтому декодированный кадр сравнивается с размером,
 * записанным в самом JPEG: если стороны переставлены местами, браузер всё сделал сам,
 * и нам остаётся только не мешать. Поймано прогоном 05.09.2026, разбор — PITFALLS §17.
 */
async function toJpegPage(
  photo: Blob,
): Promise<{ bytes: ArrayBuffer; width: number; height: number } | null> {
  const source = await photo.arrayBuffer();
  const wanted = transformFor(orientationOf(source));
  const intrinsic = sizeOf(source);

  const bitmap = await createImageBitmap(new Blob([source], { type: photo.type })).catch(() => null);
  if (!bitmap) return null;

  const quarter = wanted.rotate === 90 || wanted.rotate === 270;
  const already =
    intrinsic !== null && quarter && bitmap.width === intrinsic.height && bitmap.height === intrinsic.width;

  const rotate = already ? 0 : wanted.rotate;
  const mirror = already ? false : wanted.mirror;
  const turned = rotate === 90 || rotate === 270;
  const longest = Math.max(bitmap.width, bitmap.height);
  const scale = longest > MAX_SIDE_PX ? MAX_SIDE_PX / longest : 1;

  const width = Math.round((turned ? bitmap.height : bitmap.width) * scale);
  const height = Math.round((turned ? bitmap.width : bitmap.height) * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return null;
  }

  context.translate(width / 2, height / 2);
  context.rotate((rotate * Math.PI) / 180);
  if (mirror) context.scale(-1, 1);
  context.drawImage(
    bitmap,
    (-bitmap.width * scale) / 2,
    (-bitmap.height * scale) / 2,
    bitmap.width * scale,
    bitmap.height * scale,
  );
  bitmap.close();

  const jpeg = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!jpeg) return null;

  return { bytes: await jpeg.arrayBuffer(), width, height };
}
